// src/components/HeroKenBurns/HeroKenBurns.jsx
import { motion, useReducedMotion } from "framer-motion";
import PropTypes from "prop-types";

/**
 * Ken Burns -tyylinen hero-kuva, jossa:
 * - focal = [x%, y%] asettaa näkyvän kohdan (object-position)
 * - anchor = "top" | "center" | "bottom" siirtää transform-originia
 * - pan = { x: number, y: number } määrittää pannauksen amplitudin prosentteina
 *   (esim. {x:1.2, y:0.6} = max ±1.2% vaaka, ±0.6% pysty)
 *   Jos anchor === "top", pysty-pan on aina NEGATIIVINEN (ylöspäin), ettei yläreuna karkaa.
 */
export default function HeroKenBurns({
  image,
  alt = "",
  focal = [50, 25],
  base = 1.16,
  duration = 26,
  anchor = "top",
  pan = { x: 1.0, y: 0.6 },
}) {
  const prefersReducedMotion = useReducedMotion();

  // transform-origin ankkurin mukaan
  const originY =
    anchor === "top" ? "20%" : anchor === "bottom" ? "80%" : "50%";

  // Tee keyframe-polku. Jos anchor top, pidä y negatiivisena (zoom “poispäin” yläreunasta)
  const kf = {
    x: [`-${pan.x}%`, `${pan.x}%`, `-${pan.x * 0.7}%`, `${pan.x * 0.5}%`, "0%"],
    y:
      anchor === "top"
        ? [
            `-${pan.y}%`,
            `-${pan.y * 1.2}%`,
            `-${pan.y * 0.8}%`,
            `-${pan.y * 1.1}%`,
            `-${pan.y}%`,
          ]
        : [
            `${pan.y}%`,
            `-${pan.y}%`,
            `${pan.y * 0.6}%`,
            `-${pan.y * 0.4}%`,
            "0%",
          ],
    scale: [base, base + 0.03, base + 0.01, base + 0.035, base + 0.02],
  };

  const animate = prefersReducedMotion ? { scale: base } : kf;

  const transition = prefersReducedMotion
    ? { duration: 1 }
    : { duration, ease: "easeInOut", repeat: Infinity };

  return (
    <motion.img
      src={image}
      alt={alt}
      className="absolute inset-0 h-full w-full object-cover transform-gpu will-change-transform"
      style={{
        objectPosition: `${focal[0]}% ${focal[1]}%`,
        transformOrigin: `50% ${originY}`,
      }}
      initial={{
        scale: base,
        x: kf.x[0],
        y: kf.y[0],
        opacity: 1,
      }}
      animate={animate}
      transition={transition}
    />
  );
}

HeroKenBurns.propTypes = {
  image: PropTypes.string.isRequired,
  alt: PropTypes.string,
  focal: PropTypes.arrayOf(PropTypes.number),
  base: PropTypes.number,
  duration: PropTypes.number,
  anchor: PropTypes.oneOf(["top", "center", "bottom"]),
  pan: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
};
